import { PrivateRoute } from "@crispico/foundation-react";
import { AppMetaTempGlobals } from "@crispico/foundation-react/AppMetaTempGlobals";
import { SplitPaneExt } from "@crispico/foundation-react/components/ReactSplitPaneExt/ReactSplitPaneExt";
import { Reducers, ReduxReusableComponents, RRCProps, State } from "@crispico/foundation-react/reduxReusableComponents/ReduxReusableComponents";
import { flightEntityDescriptor } from "AppEntityDescriptors";
import { equipmentResourceEntityDescriptor } from "pages/EquipmentResource/equipmentResourceEntityDescriptor";
import React from "react";
import { RealTimeEquipmentResourceEmbeddedTable, RealTimeEquipmentResourceEmbeddedTableRRC } from "../components/tables/RealTimeEquipmentResourceEmbeddedTable";
import { RealTimeFlightEmbeddedTable, RealTimeFlightEmbeddedTableRRC } from "../components/tables/RealTimeFlightEmbeddedTable";
import { AppContainerContext } from "@crispico/foundation-react/AppContainerContext";
import { Utils } from "@crispico/foundation-react/utils/Utils";
import moment from "moment-timezone";
import { Button, Checkbox, Form, FormField, Header, Icon, Segment, SemanticICONS } from "semantic-ui-react";
import { DatePickerReactCalendar } from "@crispico/foundation-react/components/DatePicker/DatePickerReactCalendar/DatePickerReactCalendar";
import { Mission, Task } from "types";
import { DragAndDropControllerNew } from "../DragAndDropControllerNew";
import { DragAndDropArea } from "../DragAndDrop";
import { ScriptableContainer } from "@famiprog-foundation/scriptable-ui";
import { GANTT_EQUIPMENT_RESOURCE_SHOW_PAGE } from "app";

export const RT_FLIGHT_ER_OPTIONS_LOCAL_STORAGE_ITEM = "RealTimeFlightEquipmentResourcePage.options";
const REFRESH_RATE: number = 5000;

interface RealTimeFlightEquipmentResourcePageOptions {
    date: string | null,
    refreshRate: number
}
class RealTimeFlightEquipmentResourcePageState extends State {
    autoRefreshEnabled: boolean = true;
    options: RealTimeFlightEquipmentResourcePageOptions = (() => {
        const defaultOptions: RealTimeFlightEquipmentResourcePageOptions = { date: Utils.now().toISOString(), refreshRate: REFRESH_RATE };
        try {
            const raw = window.sessionStorage.getItem(RT_FLIGHT_ER_OPTIONS_LOCAL_STORAGE_ITEM);
            if (raw) {
                const lsOptions = JSON.parse(raw);
                return { ...defaultOptions, ...lsOptions };
            }
        } catch (e) {
        }
        return defaultOptions;
    })();
}

class RealTimeFlightEquipmentResourcePageReducers<S extends RealTimeFlightEquipmentResourcePageState = RealTimeFlightEquipmentResourcePageState> extends Reducers<S> {

}

type RealTimeFlightEquipmentResourcePageProps = RRCProps<RealTimeFlightEquipmentResourcePageState, RealTimeFlightEquipmentResourcePageReducers>;

export class RealTimeFlightEquipmentResourcePage extends React.Component<RealTimeFlightEquipmentResourcePageProps> {

    protected ganttEquipmentResourcesTableRef = ReduxReusableComponents.createRef<RealTimeEquipmentResourceEmbeddedTable>();
    protected ganttFlightTableRef = ReduxReusableComponents.createRef<RealTimeFlightEmbeddedTable>();

    constructor(props: RealTimeFlightEquipmentResourcePageProps) {
        super(props);
        this.getFullEntity = this.getFullEntity.bind(this);
    }

    componentDidMount() {
        this.componentDidUpdateInternal();
        AppMetaTempGlobals.appMetaInstance.tempLocationForEditorBackground = AppMetaTempGlobals.history.location;
    }

    componentWillUnmount(): void {
        AppMetaTempGlobals.appMetaInstance.tempLocationForEditorBackground = undefined;
        this.stopTimer();
    }

    componentDidUpdate(prevProps: RealTimeFlightEquipmentResourcePageProps) {
        this.componentDidUpdateInternal(prevProps);
    }

    protected isDateTodayOrFutureDay(dateStr: string | null): boolean {
        if (!dateStr) return false;
        const today = moment(Utils.now()).startOf('day');
        const selectedDate = moment(dateStr).startOf('day');
        return selectedDate.isSameOrAfter(today, 'day');
    }

    protected async componentDidUpdateInternal(prevProps?: RealTimeFlightEquipmentResourcePageProps) {
        if (!prevProps) {
            this.props.r.setInReduxState({ autoRefreshEnabled: this.isDateTodayOrFutureDay(this.props.s.options.date) });
        }
        if (prevProps && prevProps.s.options.date !== this.props.s.options.date) {
            await this.props.r.setInReduxState({ autoRefreshEnabled: this.isDateTodayOrFutureDay(this.props.s.options.date) });
            this.refreshAll(true);
        }
    }

    protected async refreshAll(changeDate: boolean) {
        console.log('🔄 refreshAll() called - stopping timer, refreshing tables, restarting timer');
        this.stopTimer();
        if (changeDate) {
            await Promise.all([
                this.ganttEquipmentResourcesTableRef.currentNotNull.setDate(this.props.s.options.date),
                this.ganttFlightTableRef.currentNotNull.setDate(this.props.s.options.date),
            ]);
        }
        await Promise.all([
            this.ganttEquipmentResourcesTableRef.currentNotNull.superRefresh(),
            this.ganttFlightTableRef.currentNotNull.superRefresh(),
        ]);

        this.startTimer();
    }

    private timer: number | undefined = undefined;

    private startTimer() {
        if (!this.props.s.autoRefreshEnabled) {
            return;
        }
        console.log('🔄 Starting timer for real-time updates');
        this.timer = window.setTimeout(async () => {
            console.log('⏰ Timer triggered - calling refreshInternal()');
            await this.refreshInternal();
            this.startTimer();
        }, this.props.s.options.refreshRate);
    }

    private stopTimer() {
        console.log('🛑 Stopping timer');
        clearTimeout(this.timer);
        this.timer = undefined;
    }

    protected async refreshInternal() {
        console.log('🔄 refreshInternal() called - refreshing tables in parallel');
        await Promise.all([
            this.ganttEquipmentResourcesTableRef.currentNotNull.refreshInternal(),
            this.ganttFlightTableRef.currentNotNull.refreshInternal(),
        ]);
    }

    getFullEntity(entityName: string, entity: any) {
        if (entityName === "Task" && !Utils.isNullOrEmpty((entity as Task).mission)) {
            // populate the mission field from HRS gantt
            return { ...entity, mission: this.ganttEquipmentResourcesTableRef.currentNotNull.getMission((entity as Task).mission.id) };
        }
        if (entityName === "Mission") {
            // populate the tasks field from Flight gantt
            return { ...entity, tasks: this.ganttFlightTableRef.currentNotNull.getTasks((entity as Mission).id) };
        }
        return entity;
    }

    renderTopBar() {
        return <Segment className="flex-container-row flex-wrap flex-center less-margin-top-bottom gap10">
            <Header as="h3" className="small-margin-top no-margin">
                <Icon name={realTimeFlightEquipmentResourcePageMenuEntry().icon as SemanticICONS} />{realTimeFlightEquipmentResourcePageMenuEntry().content}</Header>
            <Form>
                <FormField>
                    <DatePickerReactCalendar key={`date-picker`}
                        format={Utils.dateFormat} fieldName="date" allowClear={false} value={moment(this.props.s.options.date)}
                        onChange={async (value) => {
                            let newDate = value ? value.toISOString() : Utils.now().toISOString();
                            const newOptions = { ...this.props.s.options, date: newDate };
                            await this.props.r.setInReduxState({ options: newOptions });
                            window.sessionStorage.setItem(RT_FLIGHT_ER_OPTIONS_LOCAL_STORAGE_ITEM, JSON.stringify(newOptions));
                        }} />
                </FormField>
            </Form>
                <Button data-testid="RefreshButton_refresh" key="refresh" color="green" icon="refresh" onClick={async () => {
                    console.log('🔘 Manual refresh button clicked');
                    this.refreshAll(true);
                }} />
            <FormField className="small-margin-left small-margin-right">
                <Checkbox toggle label={_msg("RefreshButton.modal.header")} checked={this.props.s.autoRefreshEnabled}
                    onChange={async () => {
                        await this.props.r.setInReduxState({ autoRefreshEnabled: !this.props.s.autoRefreshEnabled });
                        if (this.props.s.autoRefreshEnabled) {
                            this.startTimer();
                        } else {
                            this.stopTimer();
                        }
                    }}
                />
            </FormField>
        </Segment>
    }

    render() {
        return <AppContainerContext.Consumer>
            {context => <>
                <Utils.Observer value={context.initializationsForClient.currentOrganizationDropdownValue} didUpdate={() => {
                    console.log('🏢 Organization changed - triggering refreshAll()');
                    this.refreshAll(true);
                }} />
                <div className="flex-container flex-grow">
                    {this.renderTopBar()}
                    <DragAndDropArea renderLayer={DragAndDropControllerNew.INSTANCE.renderDragLayer}>
                        <SplitPaneExt size={"50%"} className="RealTimeFlightEquipmentResourcePage">
                            <ScriptableContainer id={"RealTimeFlightEquipmentResourcePage_FlightTable"}>
                                <RealTimeFlightEmbeddedTableRRC ref={this.ganttFlightTableRef} id={`entityTablePage-${flightEntityDescriptor.name}`}
                                    currentLocation={AppMetaTempGlobals.history.location}
                                    entityDescriptor={flightEntityDescriptor}
                                    onRefresh={() => this.refreshAll(false)} getFullEntity={this.getFullEntity} />
                            </ScriptableContainer>
                            <ScriptableContainer id={"RealTimeFlightEquipmentResourcePage_EquipmentResourceTable"}>
                                <RealTimeEquipmentResourceEmbeddedTableRRC ref={this.ganttEquipmentResourcesTableRef} id={`entityTablePage-${equipmentResourceEntityDescriptor.name}`}
                                    currentLocation={AppMetaTempGlobals.history.location}
                                    entityDescriptor={equipmentResourceEntityDescriptor}
                                    onRefresh={() => this.refreshAll(false)} getFullEntity={this.getFullEntity} />
                            </ScriptableContainer>
                        </SplitPaneExt>
                    </DragAndDropArea>
                </div>
            </>}
        </AppContainerContext.Consumer>
    }
}

export const realTimeFlightEquipmentResourcePageUrl = "/RealTimeFlightEquipmentResourcePage";
export const realTimeFlightEquipmentResourcePageUrlRoute = () =>
    <PrivateRoute key="RealTimeFlightEquipmentResourcePage"
        path={realTimeFlightEquipmentResourcePageUrl}
        permission={GANTT_EQUIPMENT_RESOURCE_SHOW_PAGE}
        render={(props) => { return <RealTimeFlightEquipmentResourcePageRRC {...props} id="RealTimeFlightEquipmentResourcePage" /> }}
    />

export const realTimeFlightEquipmentResourcePageMenuEntry = () => {
    return {
        id: "RealTimeFlightEquipmentResourcePage",
        content: _msg("RealTimeFlightEquipmentResourcePage.label"),
        to: realTimeFlightEquipmentResourcePageUrl, exact: true, icon: "chart bar outline",
        permission: GANTT_EQUIPMENT_RESOURCE_SHOW_PAGE
    }
};

export const RealTimeFlightEquipmentResourcePageRRC = ReduxReusableComponents.connectRRC(RealTimeFlightEquipmentResourcePageState, RealTimeFlightEquipmentResourcePageReducers, RealTimeFlightEquipmentResourcePage);
